import { FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormField } from '../components/FormField';
import { PasswordField } from '../components/PasswordField';
import { StepActions } from '../components/StepActions';
import { Alert } from '../components/Alert';
import { createRegistration, RegistrationApiError } from '../../../../services/registrationApi';
import { isAcceptablePassword, isValidEmail, isValidPhone, maskPhone } from '../utils';

interface UserDataStepProps {
  registrationId: string;
  onComplete: (email: string) => void;
  onBack: () => void;
}

/** Passo 3: dados pessoais + senha. Cria o usuário e dispara o código por e-mail. */
export function UserDataStep({ registrationId, onComplete, onBack }: UserDataStepProps) {
  const { t } = useTranslation();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);

  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const validate = (): boolean => {
    const errs: Record<string, string> = {};
    if (!firstName.trim()) errs.firstName = t('views.registration.user_data.first_name_error', 'Informe seu nome.');
    if (!lastName.trim()) errs.lastName = t('views.registration.user_data.last_name_error', 'Informe seu sobrenome.');
    if (!isValidEmail(email)) errs.email = t('views.registration.user_data.email_error', 'Informe um e-mail válido.');
    if (!isValidPhone(phone)) errs.phone = t('views.registration.user_data.phone_error', 'Informe um celular válido com DDD.');
    if (!isAcceptablePassword(password)) errs.password = t('views.registration.user_data.password_error', 'A senha deve ter ao menos 8 caracteres.');
    if (confirm !== password) errs.confirm = t('views.registration.user_data.confirm_error', 'As senhas não conferem.');
    if (!acceptTerms) errs.terms = t('views.registration.user_data.terms_error', 'É necessário aceitar os termos para continuar.');
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    if (!validate()) return;

    setSubmitting(true);
    try {
      await createRegistration({
        registrationId,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        phone,
        password,
      });
      onComplete(email.trim());
    } catch (err) {
      const message =
        err instanceof RegistrationApiError && err.code === 'email_already_registered'
          ? t(
              'views.registration.user_data.email_already_registered',
              'Este e-mail já está cadastrado. Clique em “Entrar” para acessar sua conta. Caso não se lembre da senha, clique em “Recuperar senha”.',
            )
          : err instanceof RegistrationApiError && err.status === 409
            ? t('views.registration.user_data.email_taken', 'Este e-mail já possui cadastro. Tente entrar ou recuperar a senha.')
            : err instanceof RegistrationApiError
              ? err.message
              : t('views.registration.user_data.submit_error', 'Não foi possível concluir o cadastro. Tente novamente.');
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} noValidate autoComplete="on">
      {error && <Alert type="error" message={error} onClose={() => setError('')} />}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3">
        <FormField
          label={t('views.devise.registrations.name', 'Nome')}
          icon="fa-user"
          autoComplete="given-name"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          error={fieldErrors.firstName}
          required
          autoFocus
        />
        <FormField
          label={t('views.devise.registrations.last_name', 'Sobrenome')}
          icon="fa-user"
          autoComplete="family-name"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          error={fieldErrors.lastName}
          required
        />
      </div>

      <FormField
        label={t('views.login.email_placeholder', 'E-mail')}
        type="email"
        icon="fa-envelope"
        autoComplete="email"
        placeholder="voce@exemplo.com"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        error={fieldErrors.email}
        hint={t('views.registration.user_data.email_hint', 'Enviaremos o código de confirmação para este e-mail.')}
        required
      />

      <FormField
        label={t('views.devise.registrations.cellphone', 'Celular')}
        type="tel"
        icon="fa-phone"
        autoComplete="tel"
        placeholder="(00) 00000-0000"
        value={phone}
        onChange={(e) => setPhone(maskPhone(e.target.value))}
        error={fieldErrors.phone}
        maxLength={16}
        required
      />

      <PasswordField
        label={t('views.login.password_placeholder', 'Senha')}
        value={password}
        onChange={setPassword}
        error={fieldErrors.password}
        hint={t('views.registration.user_data.password_hint', 'Use ao menos 8 caracteres, com letras e números.')}
        showStrength
        required
      />

      <PasswordField
        label={t('views.devise.registrations.confirm_password', 'Confirme a senha')}
        value={confirm}
        onChange={setConfirm}
        error={fieldErrors.confirm}
        required
      />

      {/* Termos */}
      <div className="mb-2">
        <label className="flex items-start gap-2 text-[12px] text-navy-700 cursor-pointer">
          <input
            type="checkbox"
            checked={acceptTerms}
            onChange={(e) => setAcceptTerms(e.target.checked)}
            className="mt-[2px] accent-lime-500 w-[15px] h-[15px]"
            aria-invalid={!!fieldErrors.terms}
          />
          <span>
            {t('views.registration.user_data.terms_prefix', 'Li e concordo com a')}{' '}
            <a href="/app/privacy" target="_blank" rel="noopener noreferrer" className="text-navy-700 font-semibold underline">
              {t('views.registration.user_data.privacy_policy', 'Política de Privacidade')}
            </a>{' '}
            {t('views.registration.user_data.terms_suffix', 'e autorizo o tratamento dos meus dados (LGPD).')}
          </span>
        </label>
        {fieldErrors.terms && <p className="mt-[4px] text-[11px] text-alert-caution">{fieldErrors.terms}</p>}
      </div>

      <StepActions submitLabel={t('views.registration.user_data.submit', 'Continuar')} loading={submitting} loadingLabel={t('views.registration.sending', 'Enviando...')} onBack={onBack} />
    </form>
  );
}
